import React, { useState } from 'react';
import '../../css/consent-modal.scss';

const ConsentModal = ({ isOpen, onConsent, isProcessing, visualTraceEnabled = false, manualCaptureEnabled = false, aiVisualContextEnabled = false }) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleAccept = () => {
    if (isProcessing || !isChecked) return;
    onConsent(true);
  };

  const handleDecline = () => {
    if (isProcessing) return;
    onConsent(false);
  };

  if (!isOpen) return null;

  return (
    <div className="consent-modal-overlay">
      <div className="consent-modal-content">
        <div className="consent-body">
          <div className="consent-section">
            <h2>What is VizPI?</h2>
            <p>
              VizPI is an educational AI platform that tracks how students interact with AI coding assistants.
              Your interactions help researchers understand when and how to provide better support during programming assignments.
            </p>
            <p className="irb-number">IRB #26-039 — Virginia Tech</p>
          </div>

          <div className="consent-section">
            <h2>Privacy &amp; Data Collection</h2>
            <p>If you consent, we will record your activity within the platform, including:</p>
            <ul>
              <li>Mouse movements, clicks, and tab visibility</li>
              <li>Code changes, copy/paste, and terminal output</li>
              <li>Chat messages with the AI assistant</li>
              <li>Clicks, form input, and scrolling within the rendered output/preview</li>
              {visualTraceEnabled && (
                <li>Periodic screenshots and a snapshot of the page structure (HTML) of your rendered output, so researchers can see what you saw</li>
              )}
              {aiVisualContextEnabled && (
                <li>A periodic screenshot of your rendered output is also sent to the AI assistant along with your questions, so it can see what you currently see</li>
              )}
              {manualCaptureEnabled && (
                <li>Screenshots or short (up to 10-second) clips you choose to attach to a question for the AI assistant, using the camera/record buttons next to the chat box</li>
              )}
            </ul>
            <p>
              We do <strong>not</strong> collect camera or microphone data. We can detect when you leave
              the task tab, but not what you view elsewhere. All participation is through
              anonymous guest accounts — your data cannot be connected to you personally, including by
              your instructor. Data is stored on secure servers, viewable only by the research team,
              and deleted within three months.
            </p>
            <p>
              If you provide your VT email for the prize drawing, it is stored separately and never
              linked to your research data. Please do not include personally identifiable information
              in your chat messages.
            </p>
          </div>

          <div className="consent-section">
            <h2>Voluntary Participation</h2>
            <p>
              Participation is completely voluntary and will not impact your grades.
              If you decline, you can still complete the assignment but your interactions will not be recorded.
            </p>
          </div>

          <div className="consent-section">
            <h2>Questions?</h2>
            <p>
              Contact Dr. Yan Chen at{' '}
              <a href="mailto:ych@vt.edu">ych@vt.edu</a>, Shiva Pochampally at{' '}
              <a href="mailto:shivapochampally@vt.edu">shivapochampally@vt.edu</a>, or Xiaohang Tang at{' '}
              <a href="mailto:xiaohangtang@vt.edu">xiaohangtang@vt.edu</a>.{' '}
              For questions about your rights as a participant, contact the Virginia Tech HRPP Office
              at 540-231-3732 (<a href="mailto:irb@vt.edu">irb@vt.edu</a>).
            </p>
          </div>

          <div className="consent-checkbox-section">
            <label>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                disabled={isProcessing}
              />
              <span>
                I understand how VizPI works and agree to participate. I acknowledge that my interactions
                will be collected anonymously and used for educational research purposes. I understand
                that I can withdraw at any time without consequences.
              </span>
            </label>
          </div>
        </div>

        <div className="consent-footer">
          <button
            className="btn-decline"
            onClick={handleDecline}
            disabled={isProcessing}
          >
            Decline
          </button>
          <button
            className="btn-accept"
            onClick={handleAccept}
            disabled={!isChecked || isProcessing}
          >
            {isProcessing ? 'Processing...' : 'I Consent'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentModal;